/**
 * PSP/CRE Tool v2.0 - Force Echelons Constants
 * 
 * Defines the military force echelon levels supported by the application.
 * These represent the organizational hierarchy from battalion through corps.
 * 
 * IMPORTANT: FALLBACK VALUES ONLY
 * ================================
 * The typical_strength values in ECHELON_CONFIG are GENERIC FALLBACKS.
 * 
 * PRIORITY ORDER for strength values:
 * 1. User-built calculation: Σ(subordinate units) + HQ + support
 * 2. National Standards (nationalStandards.js): getDefaultStrength(nation, echelon)
 * 3. Generic Fallback (this file): ECHELON_CONFIG[echelon].typical_strength
 * 
 * Nation-specific values (examples):
 * - ROMANIAN Brigade: 4,000 (4 battalions × 600 + HQ + support)
 * - NATO Brigade: 6,666 (3 battalions × 800 + HQ + support)  
 * - US Brigade: 4,500 (3 battalions × 800 + HQ + support)
 * 
 * To add new nations (GER, FRA, POL, etc.):
 * → Update nationalStandards.js, NOT this file
 * 
 * Author: Dr. Tatar Radu MD
 * Date: November 22, 2025
 * Updated: December 18, 2024 - Added getEchelonConfig function, clarified fallback role
 * Extracted from: Tab1_ForceComposition.jsx (Phase 1)
 */

export const FORCE_ECHELONS = {
  BATTALION: 'BATTALION',
  BRIGADE: 'BRIGADE',
  DIVISION: 'DIVISION',
  CORPS: 'CORPS'
};

/**
 * GENERIC Echelon configuration - FALLBACK VALUES ONLY
 * 
 * These values are used ONLY when:
 * - No national standard is selected, OR
 * - The selected national standard doesn't define that echelon
 * 
 * For nation-specific values, use nationalStandards.js:
 * - getDefaultStrength(nationalStandard, echelon)
 * - getEchelonBreakdown(nationalStandard, echelon)
 * 
 * The strength_range values are wide to accommodate all national standards:
 * - Romanian Brigade: 4,000
 * - US Brigade: 4,500
 * - NATO Brigade: 6,666
 * All fall within range { min: 3000, max: 8000 }
 */
export const ECHELON_CONFIG = {
  BATTALION: {
    value: 'BATTALION',
    label: 'Battalion',
    typical_strength: 750,              // Fallback average (ROU:600, NATO:800, US:800)
    strength_range: { min: 300, max: 1200 },
    subordinate_echelon: null,
    parent_echelon: 'BRIGADE',
    typical_subordinates: 0,
    mtf_role: 'Role 1',
    description: 'Basic tactical unit, strength varies by nation (600-800)'
  },
  BRIGADE: {
    value: 'BRIGADE',
    label: 'Brigade',
    typical_strength: 5000,             // Fallback average (ROU:4000, US:4500, NATO:6666)
    strength_range: { min: 3000, max: 8000 },
    subordinate_echelon: 'BATTALION',
    parent_echelon: 'DIVISION',
    typical_subordinates: 4,            // Range: 3-5 depending on nation
    mtf_role: 'Role 2 Basic',
    description: 'Primary combat unit, strength varies by nation (4,000-6,666)'
  },
  DIVISION: {
    value: 'DIVISION',
    label: 'Division',
    typical_strength: 15000,            // Fallback average (ROU:12000, US:14000, NATO:20000)
    strength_range: { min: 10000, max: 25000 },
    subordinate_echelon: 'BRIGADE',
    parent_echelon: 'CORPS',
    typical_subordinates: 3,
    mtf_role: 'Role 2 Enhanced',
    description: 'Major tactical formation, strength varies by nation (12,000-20,000)'
  },
  CORPS: {
    value: 'CORPS',
    label: 'Corps',
    typical_strength: 45000,            // Fallback average (ROU:36000, US:42000, NATO:60000)
    strength_range: { min: 30000, max: 80000 },
    subordinate_echelon: 'DIVISION',
    parent_echelon: null,
    typical_subordinates: 3,
    mtf_role: 'Role 3',
    description: 'Operational-level formation, strength varies by nation (36,000-60,000)'
  }
};

/**
 * Get display name for echelon
 */
export const getEchelonDisplayName = (echelon) => {
  return ECHELON_CONFIG[echelon]?.label || echelon;
};

/**
 * Get hierarchical order (for validation)
 */
export const getEchelonOrder = (echelon) => {
  const order = {
    BATTALION: 1,
    BRIGADE: 2,
    DIVISION: 3,
    CORPS: 4
  };
  return order[echelon] || 0;
};

/**
 * Get echelon configuration
 * 
 * Returns configuration for the specified echelon, optionally using
 * national standard values when available.
 * 
 * @param {string} echelon - Echelon level (BATTALION, BRIGADE, DIVISION, CORPS)
 * @param {string} nationalStandard - Optional: ROMANIAN, NATO, US, MULTINATIONAL
 * @param {function} getDefaultStrength - Optional: function from nationalStandards.js
 * @returns {object|null} Echelon configuration or null if not found
 * 
 * Usage:
 *   // Basic (fallback values)
 *   getEchelonConfig('BRIGADE')
 * 
 *   // With national standard (recommended)
 *   import { getDefaultStrength } from '../constants/nationalStandards';
 *   getEchelonConfig('BRIGADE', 'ROMANIAN', getDefaultStrength)
 */
export const getEchelonConfig = (echelon, nationalStandard = null, getDefaultStrengthFn = null) => {
  const config = ECHELON_CONFIG[echelon];
  if (!config) return null;
  
  // If national standard provided and we have the function, override typical_strength
  if (nationalStandard && getDefaultStrengthFn) {
    const nationalStrength = getDefaultStrengthFn(nationalStandard, echelon.toLowerCase());
    if (nationalStrength) {
      return {
        ...config,
        typical_strength: nationalStrength,
        source: nationalStandard  // Track where the value came from
      };
    }
  }
  
  // Return generic fallback with source indicator
  return {
    ...config,
    source: 'GENERIC_FALLBACK'
  };
};

/**
 * Get typical strength for echelon
 * @param {string} echelon - Echelon level
 * @returns {number} Typical personnel strength
 */
export const getTypicalStrength = (echelon) => {
  return ECHELON_CONFIG[echelon]?.typical_strength || 0;
};

/**
 * Validate if strength is appropriate for echelon
 * @param {string} echelon - Echelon level
 * @param {number} strength - Personnel strength to validate
 * @returns {object} Validation result with status and message
 */
export const validateStrengthForEchelon = (echelon, strength) => {
  const config = ECHELON_CONFIG[echelon];
  if (!config) {
    return { valid: false, message: 'Unknown echelon' };
  }

  const { min, max } = config.strength_range;
  const typical = config.typical_strength;

  if (strength < min) {
    return {
      valid: false,
      severity: 'error',
      message: `Strength ${strength} below minimum ${min} for ${config.label}`
    };
  }

  if (strength > max) {
    return {
      valid: false,
      severity: 'error', 
      message: `Strength ${strength} exceeds maximum ${max} for ${config.label}`
    };
  }

  const ratio = strength / typical;
  if (ratio < 0.7 || ratio > 1.3) {
    return {
      valid: true,
      severity: 'warning',
      message: `Strength ${strength} is unusual for ${config.label} (typical: ${typical})`
    };
  }

  return { valid: true, severity: 'ok', message: 'Strength appropriate for echelon' };
};

/**
 * Get subordinate echelon
 * @param {string} echelon - Parent echelon level
 * @returns {string|null} Subordinate echelon or null
 */
export const getSubordinateEchelon = (echelon) => {
  return ECHELON_CONFIG[echelon]?.subordinate_echelon || null;
};

/**
 * Get parent echelon
 * @param {string} echelon - Child echelon level
 * @returns {string|null} Parent echelon or null
 */
export const getParentEchelon = (echelon) => {
  return ECHELON_CONFIG[echelon]?.parent_echelon || null;
};

/**
 * Get all echelons as array (for dropdowns)
 * @returns {array} Array of echelon objects with value and label
 */
export const getEchelonsArray = () => {
  return Object.values(ECHELON_CONFIG).map(config => ({
    value: config.value,
    label: config.label,
    typical_strength: config.typical_strength
  }));
};

/**
 * Check if echelon can have subordinates
 * @param {string} echelon - Echelon level
 * @returns {boolean} True if echelon can have subordinate units
 */
export const canHaveSubordinates = (echelon) => {
  return ECHELON_CONFIG[echelon]?.subordinate_echelon !== null;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const forceEchelonsConstants = {
  FORCE_ECHELONS,
  ECHELON_CONFIG,
  getEchelonDisplayName,
  getEchelonOrder,
  getEchelonConfig,
  getTypicalStrength,
  validateStrengthForEchelon,
  getSubordinateEchelon,
  getParentEchelon,
  getEchelonsArray,
  canHaveSubordinates
};

export default forceEchelonsConstants;
